/**
 * Trigger PAD Redo (SLaM Path-1 DD-change flow)
 *
 * Calls the redoPadForDdChange cloud function when a Lead clinician needs to
 * redo the Post-Assessment Discussion because the finalised Diagnostic Decision
 * disagrees with the outcome the original discussion was held under.
 *
 * What the cloud function does:
 * 1. Validates the caller is the Lead and that the completed PAD is genuinely
 *    out of date with the finalised DD (isPadStaleVsDd).
 * 2. Snapshots the full original PAD (proforma answers, notes, approved
 *    narrative, report + appointment state) into patients/{id}/padRedoArchive.
 * 3. Resets the live PAD to notStarted, clears every clinician-entered proforma
 *    answer and clinicianNotes (so both +ve→−ve and −ve→+ve start fully blank —
 *    shared Review items have no conditionsToShow gate), clears generated /
 *    approved report content, clears the stale CAR embed, unapproves any now-
 *    stale Patient Report / cover page, sets the required new PAD appointment
 *    date, and stamps redo metadata (dataModel.5_postAssessmentDiscussion.redo).
 *
 * The reopened discussion then follows the formal DD (provisional cleared).
 *
 * IMPORTANT (caller responsibility): flush any pending debounced writes BEFORE
 * calling this (so they can't clobber the reset) and re-fetch the patient AFTER
 * success, and disable the calling CTA while it is in flight to avoid a
 * double-submit (which would create two archives + a double reset).
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Trigger a redo of the Post-Assessment Discussion for a changed DD.
 *
 * @param {string} patientId - The patient ID
 * @param {string} newPadAppointmentDate - Required new PAD appointment date as a
 *   calendar date string 'YYYY-MM-DD' (both directions require a fresh appointment).
 * @param {string} [reason] - Optional reason for the redo (for audit)
 * @returns {Promise<{success: boolean, message: string, archiveId?: string, redoFromOutcome?: string, redoToOutcome?: string, error?: Error}>}
 */
export const TriggerRedoPad = async (patientId, newPadAppointmentDate, reason = null) => {
  console.log('TriggerRedoPad: Starting PAD redo for patient', patientId);

  try {
    const redoPadForDdChange = httpsCallable(functions, 'redoPadForDdChange');

    const requestData = {
      patientId,
      newPadAppointmentDate,
      reason: reason || 'Post-Assessment Discussion redone after Diagnostic Decision change',
    };

    const result = await redoPadForDdChange(requestData);

    console.log('PAD redo successful:', result.data);

    return {
      success: true,
      message: result.data.message,
      archiveId: result.data.archiveId,
      redoFromOutcome: result.data.redoFromOutcome,
      redoToOutcome: result.data.redoToOutcome,
    };
  } catch (error) {
    console.error('Error redoing PAD:', error);

    let errorMessage = 'An error occurred while redoing the Post-Assessment Discussion.';

    if (error.code === 'functions/permission-denied') {
      errorMessage = 'Only the Lead clinician can redo the Post-Assessment Discussion.';
    } else if (error.code === 'functions/failed-precondition') {
      // Surface the CF's specific precondition message when available
      // (e.g. the PAD is not out of date with the finalised DD).
      errorMessage = error.message || 'The Post-Assessment Discussion cannot be redone in its current state.';
    } else if (error.code === 'functions/invalid-argument') {
      errorMessage = error.message || 'A valid new PAD appointment date is required.';
    } else if (error.code === 'functions/not-found') {
      errorMessage = 'Patient not found.';
    } else if (error.code === 'functions/unauthenticated') {
      errorMessage = 'You must be logged in to redo the Post-Assessment Discussion.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
      error,
    };
  }
};

export default TriggerRedoPad;
